import { Model, attr, fk, many, oneToOne } from 'redux-orm';

import ActionTypes from '../constants/ActionTypes';
import Config from '../constants/Config';

export default class extends Model {
  static modelName = 'Device';

  static fields = {
    id: attr(),
    position: attr(),
    order: attr(),
    name: attr(),
    description: attr(),
    dueDate: attr(),
    timer: attr(),
    channelId: fk({
      to: 'Channel',
      as: 'channel',
      relatedName: 'devices',
    }),
    modelId: fk({
      to: 'DeviceModel',
      as: 'deviceModel',
      relatedName: 'devices',
    }),
    isSubscribed: attr({
      getDefault: () => false,
    }),
    isActionsFetching: attr({
      getDefault: () => false,
    }),
    isAllActionsFetched: attr({
      getDefault: () => false,
    }),
    items: attr({
      getDefault: () => {},
    }),
    status: attr({
      getDefault: () => 0,
    }),
    siteId: fk({
      to: 'Site',
      as: 'site',
      relatedName: 'devices',
    }),
    lineId: fk({
      to: 'Line',
      as: 'line',
      relatedName: 'devices',
    }),
    coverAttachmentId: oneToOne({
      to: 'Attachment',
      as: 'coverAttachment',
      relatedName: 'coveredDevice',
    }),
    users: many('User', 'devices'),
    labels: many('Label', 'devices'),
  };

  static reducer({ type, payload }, Device, session) {
    const { Item } = session;
    switch (type) {
      case ActionTypes.USER_TO_DEVICE_ADD: {
        const deviceModel = Device.withId(payload.deviceId);
        deviceModel.users.add(payload.id);

        if (payload.isCurrent) {
          deviceModel.isSubscribed = true;
        }

        break;
      }
      case ActionTypes.USER_FROM_DEVICE_REMOVE:
        Device.withId(payload.deviceId).users.remove(payload.id);

        break;

      case ActionTypes.ALL_DEVICES_FETCH_SUCCEEDED:
        payload.devices.forEach((device) => {
          Device.upsert(device);
        });
        break;

      case ActionTypes.SITE_FETCH_SUCCEEDED:
        payload.devices.forEach((device) => {
          Device.upsert(device);
        });

        payload.deviceMemberships.forEach(({ deviceId, userId }) => {
          Device.withId(deviceId).users.add(userId);
        });

        payload.deviceLabels.forEach(({ deviceId, labelId }) => {
          Device.withId(deviceId).labels.add(labelId);
        });

        break;
      case ActionTypes.LABEL_TO_DEVICE_ADD:
        Device.withId(payload.deviceId).labels.add(payload.id);

        break;
      case ActionTypes.LABEL_FROM_DEVICE_REMOVE:
        Device.withId(payload.deviceId).labels.remove(payload.id);

        break;
      case ActionTypes.DEVICE_FETCH_SUCCEEDED:
      case ActionTypes.DEVICE_UPDATE:
      case ActionTypes.DEVICE_COMMON_UPDATE:
        Device.withId(payload.id).update(payload.data);

        break;
      case ActionTypes.DEVICE_HIDE: {
        const data = {
          lineId: null,
          siteId: null,
        };
        Device.withId(payload.id).update(data);
        break;
      }
      case ActionTypes.DEVICE_DELETE:
        Device.withId(payload.id).deleteWithRelated();

        break;
      case ActionTypes.DEVICE_CREATE_SUCCEEDED:
        Device.upsert(payload.device);

        payload.deviceMemberships.forEach(({ deviceId, userId }) => {
          Device.withId(deviceId).users.add(userId);
        });

        payload.deviceLabels.forEach(({ deviceId, labelId }) => {
          Device.withId(deviceId).labels.add(labelId);
        });

        break;
      case ActionTypes.DEVICE_CREATE_RECEIVED:
        Device.upsert(payload.device);

        payload.deviceMemberships.forEach(({ deviceId, userId }) => {
          Device.withId(deviceId).users.add(userId);
        });

        payload.deviceLabels.forEach(({ deviceId, labelId }) => {
          Device.withId(deviceId).labels.add(labelId);
        });

        break;
      case ActionTypes.DEVICE_UPDATE_RECEIVED:
      case ActionTypes.DEVICE_COMMON_UPDATE_RECEIVED: {
        const device = Device.withId(payload.device.id);

        if (device) {
          device.update(payload.device);
        }

        break;
      }
      case ActionTypes.DEVICE_DELETE_RECEIVED:
        Device.withId(payload.device.id).deleteWithRelated();

        break;
      case ActionTypes.DEVICE_MEMBERSHIP_CREATE_RECEIVED:
        Device.withId(payload.deviceMembership.deviceId).users.add(payload.deviceMembership.userId);

        break;
      case ActionTypes.DEVICE_MEMBERSHIP_DELETE_RECEIVED:
        Device.withId(payload.deviceMembership.deviceId).users.remove(
          payload.deviceMembership.userId,
        );

        break;
      case ActionTypes.DEVICE_LABEL_CREATE_RECEIVED:
        Device.withId(payload.deviceLabel.deviceId).labels.add(payload.deviceLabel.labelId);

        break;
      case ActionTypes.DEVICE_LABEL_DELETE_RECEIVED:
        Device.withId(payload.deviceLabel.deviceId).labels.remove(payload.deviceLabel.labelId);

        break;
      case ActionTypes.ACTIONS_FETCH_REQUESTED:
        Device.withId(payload.deviceId).update({
          isActionsFetching: true,
        });

        break;
      case ActionTypes.ACTIONS_FETCH_SUCCEEDED:
        Device.withId(payload.deviceId).update({
          isActionsFetching: false,
          isAllActionsFetched: payload.actions.length < Config.ACTIONS_LIMIT,
        });

        break;
      case ActionTypes.NOTIFICATIONS_FETCH_SUCCEEDED:
        payload.devices.forEach((device) => {
          Device.upsert(device);
        });

        break;
      case ActionTypes.NOTIFICATION_CREATE_RECEIVED:
        Device.upsert(payload.device);

        break;
      case ActionTypes.DATAS_REALTIME_FETCH_SUCCEEDED:
        {
          const { items } = payload;
          items.forEach((lastItem) => {
            const device = Device.withId(lastItem.deviceId);

            if (device) {
              device.update({
                items: lastItem?.values,
                status: lastItem?.status || 0, // 0 ok, 1 timeout, 2 connection error
                loggedAt: lastItem?.loggedAt,
              });

              const allDevices = Device.all().toRefArray();
              const allItems = Item.all().toRefArray();

              allDevices.map(async (dv) => {
                if (
                  device &&
                  dv.logicDevice &&
                  dv.deviceIds.includes(device.id) &&
                  lastItem?.values
                ) {
                  const itemsDeviceList = [];
                  let status = 0;
                  let loggedAt = '';
                  allItems
                    .filter((f) => f.modelId === dv.modelId)
                    .forEach((it) => {
                      let itemValue;
                      if (it.isIntegral || it.isSum) {
                        dv.deviceIds.forEach((did) => {
                          const deviceItem = Device.withId(did);
                          if (deviceItem) {
                            if (
                              deviceItem.items &&
                              !Number.isNaN(deviceItem.items?.[it.itemName] * 1)
                            ) {
                              if (Number.isNaN(itemValue * 1)) {
                                itemValue = 0;
                              }
                              itemValue += deviceItem.items[it.itemName] * 1;
                            }
                            if (deviceItem.status !== 0) {
                              status = deviceItem.status;
                            }
                            if (loggedAt < deviceItem.loggedAt) {
                              loggedAt = deviceItem.loggedAt;
                            }
                          }
                        });
                      } else {
                        const deviceItem = Device.withId(dv.deviceIds[0]);
                        if (deviceItem) {
                          if (!Number.isNaN(deviceItem.items?.[it.itemName] * 1)) {
                            itemValue = deviceItem.items[it.itemName] * 1;
                          }
                          if (deviceItem.status !== 0) {
                            status = deviceItem.status;
                          }
                          if (loggedAt < deviceItem.loggedAt) {
                            loggedAt = deviceItem.loggedAt;
                          }
                        }
                      }
                      if (!Number.isNaN(itemValue * 1)) {
                        itemsDeviceList.push({ [it.itemName]: itemValue });
                      }
                    });

                  const itemsDevice = itemsDeviceList.reduce((result, item) => {
                    const key = Object.keys(item)[0];
                    // eslint-disable-next-line no-param-reassign
                    result[key] = item[key];
                    return result;
                  }, {});

                  await Device.withId(dv.id).update({
                    items: itemsDevice,
                    status,
                    loggedAt,
                  });
                }
              });
            }
          });
        }
        break;
      case ActionTypes.DEVICE_ITEMS_UPDATE_RECEIVED: {
        const device = Device.withId(payload.items.deviceId);

        if (device) {
          device.update({
            items: payload.items?.values,
            status: payload.items?.status || 0, // 0 ok, 1 timeout, 2 connection error
            loggedAt: payload.items?.loggedAt,
          });

          const allDevices = Device.all().toRefArray();
          const allItems = Item.all().toRefArray();

          allDevices.map(async (dv) => {
            if (dv.logicDevice && dv.deviceIds.includes(device.id) && payload.items?.values) {
              const itemsDeviceList = [];
              let status = 0;
              let loggedAt = '';
              allItems
                .filter((f) => f.modelId === dv.modelId)
                .forEach((it) => {
                  let itemValue;
                  if (it.isIntegral || it.isSum) {
                    dv.deviceIds.forEach((did) => {
                      const deviceItem = Device.withId(did);
                      if (deviceItem) {
                        if (
                          deviceItem.items &&
                          !Number.isNaN(deviceItem.items?.[it.itemName] * 1)
                        ) {
                          if (Number.isNaN(itemValue * 1)) {
                            itemValue = 0;
                          }
                          itemValue += deviceItem.items[it.itemName] * 1;
                        }
                        if (deviceItem.status !== 0) {
                          status = deviceItem.status;
                        }
                        if (loggedAt < deviceItem.loggedAt) {
                          loggedAt = deviceItem.loggedAt;
                        }
                      }
                    });
                  } else {
                    const deviceItem = Device.withId(dv.deviceIds[0]);
                    if (deviceItem) {
                      if (!Number.isNaN(deviceItem.items?.[it.itemName] * 1)) {
                        itemValue = deviceItem.items[it.itemName] * 1;
                      }
                      if (deviceItem.status !== 0) {
                        status = deviceItem.status;
                      }
                      if (loggedAt < deviceItem.loggedAt) {
                        loggedAt = deviceItem.loggedAt;
                      }
                    }
                  }
                  if (!Number.isNaN(itemValue * 1)) {
                    itemsDeviceList.push({ [it.itemName]: itemValue });
                  }
                });

              const itemsDevice = itemsDeviceList.reduce((result, item) => {
                const key = Object.keys(item)[0];
                // eslint-disable-next-line no-param-reassign
                result[key] = item[key];
                return result;
              }, {});

              await Device.withId(dv.id).update({
                items: itemsDevice,
                status,
                loggedAt,
              });
            }
          });
        }

        break;
      }
      default:
    }
  }

  static getOrderedQuerySet() {
    return this.orderBy(['modelId', 'order']);
  }

  getOrderedAlarmsQuerySet() {
    return this.alarms.orderBy('id');
  }

  getOrderedAttachmentsQuerySet() {
    return this.attachments.orderBy('id', false);
  }

  getOrderedInDeviceActionsQuerySet() {
    return this.actions.orderBy('id', false);
  }

  getUnreadNotificationsQuerySet() {
    return this.notifications.filter({
      isRead: false,
    });
  }

  deleteWithRelated() {
    this.alarms.delete();
    this.actions.delete();
    this.alertSettings.delete();
    this.alerts.delete();
    this.alertOccurs.delete();
    this.alertRess.delete();
    this.notifications.delete();

    this.delete();
  }
}
